import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import {
  LateralCandidateForm,
  LateralCandidateFormData,
  emptyLateralCandidateForm,
} from "@/components/lateral/LateralCandidateForm";
import type { LateralCandidate } from "@/types/database";

export default function EditLateralCandidatePage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState<LateralCandidateFormData>(emptyLateralCandidateForm());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: candidate, isLoading } = useQuery({
    queryKey: ["lateral-candidate", candidateId],
    queryFn: async () => {
      if (!candidateId) throw new Error("Candidate ID required");
      const { data, error } = await supabase
        .from("lateral_candidates")
        .select("*")
        .eq("id", candidateId)
        .single();
      if (error) throw error;
      return data as LateralCandidate;
    },
    enabled: !!candidateId,
  });

  useEffect(() => {
    if (!candidate) return;
    setForm({
      full_name: candidate.full_name,
      email: candidate.email ?? "",
      phone: candidate.phone ?? "",
      whatsapp_number: candidate.whatsapp_number ?? "",
      current_location: candidate.current_location ?? "",
      permanent_address: candidate.permanent_address ?? "",
      linkedin_url: candidate.linkedin_url ?? "",
      github_url: candidate.github_url ?? "",
      lateral_job_position_id: candidate.lateral_job_position_id ?? "",
      notes: candidate.notes ?? "",
      current_company: candidate.current_company ?? "",
      current_designation: candidate.current_designation ?? "",
      total_experience_years:
        candidate.total_experience_years != null ? String(candidate.total_experience_years) : "",
      relevant_experience_years:
        candidate.relevant_experience_years != null
          ? String(candidate.relevant_experience_years)
          : "",
      current_ctc: candidate.current_ctc ?? "",
      in_hand_salary: candidate.in_hand_salary ?? "",
      expected_ctc: candidate.expected_ctc ?? "",
      notice_period: candidate.notice_period ?? "",
      key_skills: candidate.key_skills?.join(", ") ?? "",
      technologies: candidate.technologies?.join(", ") ?? "",
      total_companies:
        candidate.total_companies != null ? String(candidate.total_companies) : "",
      avg_tenure_per_company:
        candidate.avg_tenure_per_company != null ? String(candidate.avg_tenure_per_company) : "",
      why_hire_you: candidate.why_hire_you ?? "",
      reason_for_job_change: candidate.reason_for_job_change ?? "",
      why_join_us: candidate.why_join_us ?? "",
      top_5_projects: candidate.top_5_projects ?? "",
      college_name: candidate.college_name ?? "",
      highest_qualification: candidate.highest_qualification ?? "",
      interview_preferred_date: candidate.interview_preferred_date ?? "",
      interview_preferred_time: candidate.interview_preferred_time ?? "",
    });
  }, [candidate]);

  const handleChange = (key: keyof LateralCandidateFormData, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidateId || !form.full_name.trim()) return;
    setIsSubmitting(true);
    try {
      const toArr = (s: string) =>
        s.split(",").map((v) => v.trim()).filter(Boolean);
      const { error } = await supabase
        .from("lateral_candidates")
        .update({
          full_name: form.full_name,
          email: form.email || null,
          phone: form.phone || null,
          whatsapp_number: form.whatsapp_number || null,
          current_location: form.current_location || null,
          permanent_address: form.permanent_address || null,
          linkedin_url: form.linkedin_url || null,
          github_url: form.github_url || null,
          lateral_job_position_id: form.lateral_job_position_id || null,
          notes: form.notes || null,
          current_company: form.current_company || null,
          current_designation: form.current_designation || null,
          total_experience_years: form.total_experience_years
            ? parseFloat(form.total_experience_years)
            : null,
          relevant_experience_years: form.relevant_experience_years
            ? parseFloat(form.relevant_experience_years)
            : null,
          current_ctc: form.current_ctc || null,
          in_hand_salary: form.in_hand_salary || null,
          expected_ctc: form.expected_ctc || null,
          notice_period: form.notice_period || null,
          key_skills: form.key_skills ? toArr(form.key_skills) : null,
          technologies: form.technologies ? toArr(form.technologies) : null,
          total_companies: form.total_companies ? parseInt(form.total_companies) : null,
          avg_tenure_per_company: form.avg_tenure_per_company
            ? parseFloat(form.avg_tenure_per_company)
            : null,
          why_hire_you: form.why_hire_you || null,
          reason_for_job_change: form.reason_for_job_change || null,
          why_join_us: form.why_join_us || null,
          top_5_projects: form.top_5_projects || null,
          college_name: form.college_name || null,
          highest_qualification: form.highest_qualification || null,
          interview_preferred_date: form.interview_preferred_date || null,
          interview_preferred_time: form.interview_preferred_time || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", candidateId);
      if (error) throw error;
      toast({ title: "Candidate updated" });
      navigate(`/admin/lateral/candidates/${candidateId}/evaluate`);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <div className="text-center py-8 text-gray-500">Loading...</div>;
  if (!candidate) return <div className="text-center py-8 text-gray-500">Candidate not found</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Candidate</h1>
          <p className="text-sm text-gray-500">{candidate.full_name}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <LateralCandidateForm values={form} onChange={handleChange} />
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}
